import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Modal,
  Row,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  MailOutlined,
  PhoneOutlined,
  ReloadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import CopyableText from "common/CopyableText";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useUsers from "hooks/userManagement/useUsers";
import { useFileUpload } from "hooks/useFileUpload";
import {
  fetchWorkspace,
  fetchWorkspaceInvitePrivileges,
  fetchWorkspaceInviteRoles,
  fetchWorkspaceMembershipsForUser,
  fetchWorkspaceMembers,
} from "services/workspaceService";
import apiClient from "utils/apiClient";
import paths from "routes/paths";
import {
  formatDateTime,
  membershipPrivilegeNames,
  membershipRoleNames,
  membershipUserEmail,
  membershipUserId,
  membershipUserLabel,
  readable,
  statusColor,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles, workspaceStyles } from "./colors";

const { Text, Title } = Typography;
const OLITASK_ACCESS_CODE = "OLITASK";

const cleanValue = (value) => (value == null ? "" : String(value).trim());

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);

const valueFrom = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "") || "-";

const roleIdFrom = (item) => item?.roleId || item?.role?.id || item?.id || "";
const privilegeIdFrom = (item) =>
  item?.privilegeId || item?.privilege?.id || item?.id || "";

const roleLabelFrom = (item, fallbackRole) =>
  valueFrom(
    item?.role?.name,
    item?.role?.code,
    item?.name,
    item?.code,
    fallbackRole?.name,
    fallbackRole?.code,
    roleIdFrom(item),
  );

const privilegeLabelFrom = (item, fallbackPrivilege) =>
  valueFrom(
    item?.privilege?.name,
    item?.privilege?.scope,
    item?.privilege?.code,
    item?.name,
    item?.scope,
    item?.code,
    fallbackPrivilege?.name,
    fallbackPrivilege?.scope,
    fallbackPrivilege?.code,
    privilegeIdFrom(item),
  );

const fileUrlFromValue = (value, visited = new Set()) => {
  if (!value) return null;
  if (typeof value === "string") {
    return /^(https?:|data:|blob:)/i.test(value) ? value : null;
  }
  if (typeof value !== "object" || visited.has(value)) return null;
  visited.add(value);

  const direct = [
    value.signedUrl,
    value.signed_url,
    value.downloadUrl,
    value.download_url,
    value.publicUrl,
    value.public_url,
    value.fileUrl,
    value.file_url,
    value.previewUrl,
    value.preview_url,
    value.uri,
    value.url,
  ].find((item) => typeof item === "string" && /^(https?:|data:|blob:)/i.test(item));
  if (direct) return direct;

  return [value.file, value.metadata, value.meta, value.data]
    .map((item) => fileUrlFromValue(item, visited))
    .find(Boolean) || null;
};

const fileIdFromValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return /^(https?:|data:|blob:)/i.test(value) ? "" : value;
  if (typeof value !== "object") return "";
  return cleanValue(
    value.id ||
      value.fileId ||
      value.file_id ||
      value.fid ||
      value.file?.id ||
      value.file?.fileId ||
      value.metadata?.id ||
      value.meta?.id,
  );
};

const readPhotoUrl = async (value, getFileMetadata, getFileSignedUrl) => {
  const raw = cleanValue(value);
  const directUrl = fileUrlFromValue(value);
  if (directUrl) return directUrl;

  const fileId = fileIdFromValue(value) || raw;
  if (!fileId || /^(https?:|data:|blob:)/i.test(fileId)) return null;

  try {
    const meta = getFileMetadata ? await getFileMetadata(fileId) : null;
    const metaUrl = fileUrlFromValue(meta);
    if (metaUrl) return metaUrl;
  } catch {
    // Fall through to signed URL lookup.
  }

  try {
    return getFileSignedUrl ? await getFileSignedUrl(fileId) : null;
  } catch {
    return null;
  }
};

const displayName = (user, membership) => {
  const name = [user?.firstName, user?.middleName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || user?.name || membershipUserLabel(membership) || user?.email || "User";
};

const accessCount = (membership) =>
  membershipRoleNames(membership).length + membershipPrivilegeNames(membership).length;

const mergeMembership = (...memberships) => {
  const validMemberships = memberships.filter(Boolean);
  if (!validMemberships.length) return null;

  return validMemberships.reduce((merged, item) => {
    const itemRoles = membershipRoleNames(item);
    const itemPrivileges = membershipPrivilegeNames(item);
    return {
      ...merged,
      ...item,
      user: {
        ...(merged.user || {}),
        ...(item.user || {}),
      },
      roleNames: itemRoles.length ? itemRoles : membershipRoleNames(merged),
      privilegeScopes: itemPrivileges.length
        ? itemPrivileges
        : membershipPrivilegeNames(merged),
    };
  }, {});
};

const WorkspaceMemberDetailsPage = () => {
  const { workspaceId, userId } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const actorId = authUser?.id || authUser?.userId || "";
  const { getUserById } = useUsers(0, 20, null, true, { autoFetch: false });
  const { getFileMetadata, getFileSignedUrl } = useFileUpload();
  const [account, setAccount] = useState(null);
  const [membership, setMembership] = useState(null);
  const [memberUser, setMemberUser] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [userPrivileges, setUserPrivileges] = useState([]);

  const loadData = useCallback(async () => {
    if (!workspaceId || !userId || !actorId) return;

    try {
      setLoading(true);
      const nextAccount = await fetchWorkspace(workspaceId, actorId);
      setAccount(nextAccount || {});

      let nextMembership =
        (nextAccount?.users || []).find((item) => membershipUserId(item) === userId) || null;

      try {
        const [workspaceMembersResult, userMembershipsResult] = await Promise.allSettled([
          fetchWorkspaceMembers({
            actorId,
            workspaceId: workspaceId,
            size: 100,
          }),
          fetchWorkspaceMembershipsForUser({
            actorId,
            userId,
            size: 100,
          }),
        ]);
        const workspaceMember =
          workspaceMembersResult.status === "fulfilled"
            ? (workspaceMembersResult.value?.content || []).find(
                (item) => membershipUserId(item) === userId,
              )
            : null;
        const userWorkspaceMembership =
          userMembershipsResult.status === "fulfilled"
            ? (userMembershipsResult.value?.content || []).find(
                (item) => item?.workspaceId === workspaceId,
              )
            : null;
        const orderedMemberships = [
          nextMembership,
          workspaceMember,
          userWorkspaceMembership,
        ].sort((a, b) => accessCount(a) - accessCount(b));
        nextMembership = mergeMembership(...orderedMemberships) || nextMembership;
      } catch {
        // Account payload membership is enough for the minimal view.
      }

      setMembership(nextMembership);

      let nextUser = null;
      try {
        nextUser = await getUserById(userId);
      } catch {
        nextUser = nextMembership?.user || { id: userId };
      }
      setMemberUser(nextUser);

      try {
        const [
          roleDefinitionsResult,
          privilegeDefinitionsResult,
          roleResponse,
          privilegeResponse,
        ] = await Promise.all([
          fetchWorkspaceInviteRoles(),
          fetchWorkspaceInvitePrivileges(),
          apiClient.get(`/identity-service/api/v1/roles/user/${userId}`, {
            params: { code: OLITASK_ACCESS_CODE },
          }),
          apiClient.get(`/identity-service/api/v1/privileges/user/${userId}`, {
            params: { code: OLITASK_ACCESS_CODE },
          }),
        ]);
        const rolesById = Object.fromEntries(
          (roleDefinitionsResult || []).map((role) => [role.id, role]),
        );
        const privilegesById = Object.fromEntries(
          (privilegeDefinitionsResult || []).map((privilege) => [privilege.id, privilege]),
        );
        setUserRoles(
          pageContent(roleResponse.data)
            .map((item) => {
              const id = roleIdFrom(item);
              if (!id) return null;
              return {
                id,
                label: roleLabelFrom(item, rolesById[id]),
              };
            })
            .filter(Boolean),
        );
        setUserPrivileges(
          pageContent(privilegeResponse.data)
            .map((item) => {
              const id = privilegeIdFrom(item);
              if (!id) return null;
              return {
                id,
                label: privilegeLabelFrom(item, privilegesById[id]),
              };
            })
            .filter(Boolean),
        );
      } catch (error) {
        setUserRoles([]);
        setUserPrivileges([]);
        message.error(error?.message || "Failed to load member access.");
      }

      setPhotoUrl(
        await readPhotoUrl(
          nextUser?.profilePhoto ||
            nextUser?.photo ||
            nextUser?.avatar ||
            nextMembership?.user?.profilePhoto ||
          nextMembership?.user?.photo ||
            nextMembership?.user?.avatar,
          getFileMetadata,
          getFileSignedUrl,
        ),
      );
    } catch (error) {
      message.error(error?.message || "Failed to load workspace member.");
    } finally {
      setLoading(false);
    }
  }, [actorId, getFileMetadata, getFileSignedUrl, getUserById, workspaceId, userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const roles = userRoles;
  const privileges = userPrivileges;
  const email = memberUser?.email || membershipUserEmail(membership);
  const phone = cleanValue(memberUser?.phone);
  const gender = cleanValue(memberUser?.gender);
  const name = useMemo(() => displayName(memberUser, membership), [memberUser, membership]);
  const membershipStatus = readable(membership?.membershipStatus, "Membership");
  const userStatus = readable(memberUser?.status, "-");
  const joinedAt = formatDateTime(membership?.createdAt) || "-";
  const updatedAt = formatDateTime(membership?.updatedAt || memberUser?.updatedAt) || "-";

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={<UserOutlined />}
          title="Workspace Member"
          subtitle="Minimal user and membership details"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Space wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            style={workspaceButtonStyles.back}
            onClick={() => navigate(paths.workspaceDetails(workspaceId))}
          >
            Back
          </Button>
          <Button
            icon={<ReloadOutlined />}
            style={workspaceButtonStyles.refresh}
            onClick={loadData}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {loading && !memberUser ? (
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      ) : !memberUser && !membership ? (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Workspace member not found" />
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card
            style={{
              borderRadius: 8,
              borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
              background: `linear-gradient(135deg, ${OLIVEEB_COLORS.card} 0%, ${OLIVEEB_COLORS.oliveebBlueSoft} 48%, ${OLIVEEB_COLORS.oliveebGreenSoft} 100%)`,
              boxShadow: "0 18px 42px rgba(9, 3, 89, 0.12)",
            }}
            bodyStyle={{ padding: 22 }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 18,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  if (photoUrl) setPhotoPreviewOpen(true);
                }}
                disabled={!photoUrl}
                aria-label="Preview profile photo"
                style={{
                  border: `3px solid ${OLIVEEB_COLORS.card}`,
                  background: "transparent",
                  padding: 0,
                  cursor: photoUrl ? "zoom-in" : "default",
                  borderRadius: "50%",
                  boxShadow: "0 14px 30px rgba(15, 23, 42, 0.18)",
                  flex: "0 0 auto",
                }}
              >
                <Avatar
                  src={photoUrl || undefined}
                  icon={!photoUrl ? <UserOutlined /> : undefined}
                  style={{
                    background: workspaceStyles.iconBubble.background,
                    color: workspaceStyles.iconBubble.color,
                    fontSize: 34,
                  }}
                  size={88}
                >
                  {!photoUrl ? String(name || "U").slice(0, 1).toUpperCase() : null}
                </Avatar>
              </button>
              <div style={{ minWidth: 220, flex: 1 }}>
                <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
                  {account?.name || "Workspace"}
                </Text>
                <Title level={3} style={{ margin: "2px 0 4px", color: OLIVEEB_COLORS.oliveebDarkBlue }}>
                  {name}
                </Title>
                <Space wrap size={[8, 6]}>
                  <Tag color={statusColor(membership?.membershipStatus)} style={{ marginInlineEnd: 0 }}>
                    {membershipStatus}
                  </Tag>
                  {userStatus && userStatus !== "-" ? (
                    <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                      {userStatus}
                    </Tag>
                  ) : null}
                  {gender ? (
                    <Tag color="green" style={{ marginInlineEnd: 0 }}>
                      {readable(gender)}
                    </Tag>
                  ) : null}
                </Space>
              </div>
              <Space direction="vertical" size={6} style={{ minWidth: 220 }}>
                {email && email !== "None" ? (
                  <Text style={{ color: OLIVEEB_COLORS.textPrimary, wordBreak: "break-word" }}>
                    <MailOutlined style={{ color: OLIVEEB_COLORS.oliveebBlue }} /> {email}
                  </Text>
                ) : null}
                {phone && phone !== "None" ? (
                  <Text style={{ color: OLIVEEB_COLORS.textPrimary, wordBreak: "break-word" }}>
                    <PhoneOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} /> {phone}
                  </Text>
                ) : null}
              </Space>
            </div>
          </Card>

          {!membership ? (
            <Alert
              type="warning"
              showIcon
              message="Membership details are not available."
              description="Showing only the available user profile details."
            />
          ) : null}

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card
                title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>User Profile</Text>}
                style={{ borderRadius: 8, height: "100%", borderColor: OLIVEEB_COLORS.oliveebGreenSoft }}
              >
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  items={[
                    {
                      key: "email",
                      label: "Email",
                      children: email && email !== "None" ? email : "-",
                    },
                    {
                      key: "phone",
                      label: "Phone",
                      children: phone && phone !== "None" ? phone : "-",
                    },
                    {
                      key: "gender",
                      label: "Gender",
                      children: gender ? readable(gender) : "-",
                    },
                    {
                      key: "username",
                      label: "Username",
                      children: memberUser?.username || "-",
                    },
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Workspace Details</Text>}
                style={{ borderRadius: 8, height: "100%", borderColor: OLIVEEB_COLORS.oliveebBlueSoft }}
              >
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  items={[
                    {
                      key: "workspace",
                      label: "Workspace",
                      children: account?.name || "-",
                    },
                    {
                      key: "userId",
                      label: "User ID",
                      children: userId ? (
                        <CopyableText value={userId} />
                      ) : (
                        "-"
                      ),
                    },
                    {
                      key: "joined",
                      label: "Joined",
                      children: joinedAt,
                    },
                    {
                      key: "updated",
                      label: "Updated",
                      children: updatedAt,
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>

          <Modal
            open={photoPreviewOpen}
            onCancel={() => setPhotoPreviewOpen(false)}
            footer={null}
            centered
            width={420}
            title="Profile Photo"
          >
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={`${name} profile`}
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  borderRadius: 8,
                  display: "block",
                }}
              />
            ) : null}
          </Modal>

          <Card
            title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Access</Text>}
            extra={
              <Button size="small" onClick={() => navigate(paths.accessControl)}>
                Access Control
              </Button>
            }
            style={{ borderRadius: 8 }}
          >
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <div>
                <Text type="secondary">Roles</Text>
                <div style={{ marginTop: 6 }}>
                  {roles.length ? roles.map((role) => (
                    <Tag key={role.id} color="blue">{readable(role.label)}</Tag>
                  )) : <Text>-</Text>}
                </div>
              </div>
              <div>
                <Text type="secondary">Privileges</Text>
                <div style={{ marginTop: 6 }}>
                  {privileges.length ? privileges.map((privilege) => (
                    <Tag key={privilege.id} color="cyan">{readable(privilege.label)}</Tag>
                  )) : <Text>-</Text>}
                </div>
              </div>
            </Space>
          </Card>
        </Space>
      )}
    </div>
  );
};

export default WorkspaceMemberDetailsPage;
